/**
 * Lane C0-OUTFIT2X, all outfits: every garment factory of the parts library in a native (figure-grid) version, with
 * its painting worked out from the design itself, so each look keeps its cut, colours and motifs and gains the full
 * detail: materials (cotton, figured silk, leather, gold and other metals, jade), light from the true side, folds down
 * skirts and robes, the design's hem motif as an embroidered band between pipings, cuffs, woven brocade, piping on the
 * trims, stitched belts; hair and fixed-colour headwear relit at full resolution.
 *
 * sets.ts `appearanceOf` takes these when `outfit2x()` is on; the uniform and fashion outfits go through
 * `nativeUniformLayers` / `nativeFashionLayers` (the same slots as uniforms.ts / fashion.ts).
 */
import { shade, toHsl } from '../../palette';
import {
  type BottomLook,
  BOTTOMS,
  type Brocade,
  bottomLayer,
  type HemMotif,
  robeLayer,
  type RobeLook,
  ROBES,
  topLayer,
  type TopLook,
} from '../clothes';
import { type Extra, extraLayer } from './../flowParts';
import { headwearLayer, type Layer, type Piece } from '../figure';
import type { OutfitBody } from '../fashionData';
import {
  beltLayer,
  type BeltLook,
  capeLayer,
  type CapeLook,
  pauldronLayer,
  type PauldronLook,
  SHOES,
  shoesLayer,
  type ShoeLook,
  stoleLayer,
  type StoleLook,
} from '../gear';
import { HEADWEAR_BY_ID } from '../headwear';
import { type Regalia, regaliaLayer } from '../regalia';
import type { Uniform } from '../uniforms';
import { type Band, type Colours, type Dress, nativeLayer, paintCloth, type ReadPass } from './garments';
import { nativeHairLayer } from './hair2x';
import { BLOSSOM, CLOUD, FRET, type Mat, mat, type MatKind, type Role, Roles, sentinel } from './paint';

// ── materials from colours ──

/** A gold-coloured thread or fitting reads as metal. */
export function goldish(hex: string): boolean {
  const [h, s, l] = toHsl(hex);
  return h >= 28 && h <= 60 && s > 0.35 && l > 0.38 && l < 0.88;
}
/** Pale green-white or green: jade. */
const jadeish = (hex: string) => {
  const [h, s, l] = toHsl(hex);
  return h >= 120 && h <= 175 && s > 0.2 && l > 0.45;
};
/** A colour's material: gold and jade by their look, else the given cloth. */
export const auto = (hex: string, cloth: MatKind = 'silk'): Mat =>
  goldish(hex) ? mat(hex, 'gold') : jadeish(hex) ? mat(hex, 'jade') : mat(hex, cloth);

// ── motifs ──

const WAVES = ['..##....', '.#..#...', '#....#.#', '.####.#.', '#....#..'];
const FLAMES = ['...#....', '..##...#', '.#.#..##', '#..##.#.', '#...###.'];
const LOTUS = ['...#...', '..#.#..', '.#.#.#.', '#..#..#', '.#####.'];
const BAMBOO = ['.#...#.', '.#..##.', '.##.#..', '.#...#.', '.#...#.'];

const HEM: Record<HemMotif, readonly string[]> = {
  waves: WAVES,
  clouds: CLOUD,
  flames: FLAMES,
  lotus: LOTUS,
  bamboo: BAMBOO,
  peony: LOTUS,
};

/** A hem band with no motif (four plain rows). */
const PLAIN_BAND = ['........', '........', '........', '........'];

const xs = (m: readonly string[]) => m.map((r) => r.replace(/#/g, 'x'));
const BROCADE: Record<Brocade, { motif: readonly string[]; sx: number; sy: number }> = {
  lattice: { motif: ['..x..', '.x.x.', 'x...x', '.x.x.', '..x..'], sx: 8, sy: 8 },
  clouds: { motif: ['.xx..', 'x..x.', '.o.x.'], sx: 10, sy: 8 },
  scales: { motif: ['.xxx.', 'x...x'], sx: 6, sy: 5 },
  blossoms: { motif: BLOSSOM, sx: 10, sy: 9 },
  fret: { motif: xs(FRET), sx: 8, sy: 7 },
};

// ── garments ──

export function nTop(look: TopLook): Layer {
  const c: Colours = { cloth: look.colour, trim: look.trim, inner: look.inner };
  if (look.accent) c.accent = look.accent;
  return nativeLayer(
    (s) => topLayer({ ...look, colour: s.cloth!, trim: s.trim!, inner: s.inner!, ...(look.accent ? { accent: s.accent! } : {}) }),
    c,
    {
      mats: {
        cloth: mat(look.colour, look.brocade ? 'silk' : 'cotton'),
        trim: auto(look.trim),
        inner: auto(look.inner, 'cotton'),
        ...(look.accent ? { accent: auto(look.accent) } : {}),
      },
      keepLines: true,
      ...(look.brocade ? { brocade: BROCADE[look.brocade] } : {}),
      ...(look.accent ? { piping: auto(look.accent) } : {}),
    },
  );
}

export function nRobe(look: RobeLook, read?: ReadPass): Layer {
  const design = ROBES.find((r) => r.id === look.id);
  const c: Colours = { cloth: look.colour, trim: look.trim, inner: look.inner, sash: look.sash };
  if (look.accent) c.accent = look.accent;
  const rich = !!(look.brocade || look.hem);
  const thread = look.accent ?? look.inner;
  const hem: Band | undefined = look.hem
    ? { motif: HEM[look.hem], ground: auto(look.trim), thread: auto(thread), piping: auto(look.accent ?? '#d6a640') }
    : undefined;
  const dress: Dress = {
    mats: {
      cloth: mat(look.colour, rich ? 'silk' : 'cotton'),
      trim: auto(look.trim),
      inner: auto(look.inner, 'cotton'),
      sash: auto(look.sash),
      ...(look.accent ? { accent: auto(look.accent) } : {}),
    },
    keepLines: true,
    ...(hem ? { hem } : {}),
    ...(design && design.sleeve !== 'none' ? { cuff: { ground: auto(look.trim), piping: auto(thread), rows: 3 } } : {}),
    ...(look.brocade ? { brocade: BROCADE[look.brocade] } : {}),
    ...(look.accent ? { piping: auto(look.accent) } : {}),
    ...(read ? { read } : {}),
  };
  return nativeLayer(
    (s) =>
      robeLayer({
        ...look,
        colour: s.cloth!,
        trim: s.trim!,
        inner: s.inner!,
        sash: s.sash!,
        ...(look.accent ? { accent: s.accent! } : {}),
      }),
    c,
    dress,
  );
}

export function nBottom(look: BottomLook): Layer {
  const d = BOTTOMS.find((b) => b.id === look.id);
  return nativeLayer((s) => bottomLayer({ ...look, colour: s.cloth!, wrap: s.wrap! }), { cloth: look.colour, wrap: look.wrap }, {
    mats: { cloth: mat(look.colour, d?.fit === 'skirt' ? 'silk' : 'cotton'), wrap: auto(look.wrap, 'cotton') },
    keepLines: true,
  });
}

export function nShoes(look: ShoeLook): Layer {
  const d = SHOES.find((x) => x.id === look.id);
  const kind: MatKind = d && d.shaft !== 'none' ? 'leather' : d?.toe ? 'silk' : 'cotton';
  return nativeLayer((s) => shoesLayer({ ...look, colour: s.cloth! }), { cloth: look.colour }, { mats: { cloth: mat(look.colour, kind) }, keepLines: true });
}

const SILK_BELTS = new Set(['bow', 'obi', 'tassels']);
export function nBelt(look: BeltLook): Layer {
  const id = look.id ?? 'leather';
  const kind: MatKind = SILK_BELTS.has(id) ? 'silk' : id === 'rope' ? 'cotton' : id === 'jade' ? 'jade' : 'leather';
  return nativeLayer((s) => beltLayer({ ...look, colour: s.cloth!, buckle: s.extra! }), { cloth: look.colour, extra: look.buckle }, {
    mats: { cloth: goldish(look.colour) ? mat(look.colour, 'gold') : mat(look.colour, kind), extra: auto(look.buckle, 'lacquer') },
    stitch: kind === 'leather',
    keepLines: true,
  });
}

export function nCape(look: CapeLook): Layer {
  const c: Colours = { cloth: look.colour, extra: look.clasp };
  if (look.trim) c.trim = look.trim;
  return nativeLayer(
    (s) => capeLayer({ ...look, colour: s.cloth!, clasp: s.extra!, ...(look.trim ? { trim: s.trim! } : {}) }),
    c,
    {
      mats: { cloth: mat(look.colour, look.trim ? 'silk' : 'cotton'), extra: auto(look.clasp, 'lacquer'), ...(look.trim ? { trim: auto(look.trim) } : {}) },
      keepLines: true,
    },
  );
}

export function nPauldrons(look: PauldronLook): Layer {
  return nativeLayer((s) => pauldronLayer({ ...look, plate: s.cloth!, edge: s.trim! }), { cloth: look.plate, trim: look.edge }, {
    mats: { cloth: auto(look.plate, 'lacquer'), trim: auto(look.edge, 'lacquer') },
    keepLines: true,
  });
}

export function nStole(look: StoleLook): Layer {
  return nativeLayer((s) => stoleLayer({ fur: s.cloth! }), { cloth: look.fur }, { mats: { cloth: mat(look.fur, 'cotton') }, keepLines: true });
}

export function nRegalia(list: readonly Regalia[]): Layer {
  const real: Colours = {};
  const mats: Partial<Record<Role, Mat>> = {};
  list.forEach((r, i) => {
    for (const [k, hex] of [
      [0, r.c],
      [1, r.s],
      [2, r.a],
    ] as const) {
      const role = `c${i * 3 + k}` as Role;
      real[role] = hex;
      mats[role] = auto(hex);
    }
  });
  return nativeLayer(
    (s) => regaliaLayer(list.map((r, i) => ({ ...r, c: s[`c${i * 3}`]!, s: s[`c${i * 3 + 1}`]!, a: s[`c${i * 3 + 2}`]! }))),
    real,
    { mats, keepLines: true },
  );
}

export function nExtra(e: Extra): Layer {
  if (e.id === 'scarf')
    return nativeLayer((s) => extraLayer({ ...e, colour: s.cloth! }), { cloth: e.colour }, { mats: { cloth: mat(e.colour, 'silk') }, keepLines: true });
  if (e.id === 'sleeves')
    return nativeLayer((s) => extraLayer({ ...e, colour: s.cloth!, trim: s.trim! }), { cloth: e.colour, trim: e.trim }, {
      mats: { cloth: mat(e.colour, 'silk'), trim: auto(e.trim) },
      keepLines: true,
    });
  const c: Colours = { cloth: e.colour };
  if (e.edge) c.trim = e.edge;
  if (e.clasp) c.extra = e.clasp;
  return nativeLayer(
    (s) => extraLayer({ ...e, colour: s.cloth!, ...(e.edge ? { edge: s.trim! } : {}), ...(e.clasp ? { clasp: s.extra! } : {}) }),
    c,
    { mats: { cloth: mat(e.colour, 'cotton'), ...(e.edge ? { trim: auto(e.edge) } : {}), ...(e.clasp ? { extra: auto(e.clasp, 'lacquer') } : {}) }, keepLines: true },
  );
}

// ── hair and headwear ──

export const nHair = (style: string, colour: string): Layer => nativeHairLayer(style, colour);

/**
 * Fixed-colour headwear relit: its colours grouped by hue (a colour and its hand-picked shades are one material), each
 * group drawn in a stand-in colour (the members' lightness steps kept) and painted as gold, jade or cloth.
 */
export function nHeadwear(id: string): Layer {
  const item = HEADWEAR_BY_ID[id];
  if (!item) return headwearLayer(id);
  const groups: { rep: string; members: [string, string][] }[] = [];
  for (const [ch, hex] of Object.entries(item.colours)) {
    const [h, s, l] = toHsl(hex);
    const g = groups.find((q) => {
      const [h2, s2] = toHsl(q.rep);
      const grey = s < 0.15 && s2 < 0.15;
      const dh = Math.abs(((h - h2 + 540) % 360) - 180);
      return grey || (s >= 0.15 && s2 >= 0.15 && dh < 22);
    });
    if (g) g.members.push([ch, hex]);
    else groups.push({ rep: hex, members: [[ch, hex]] });
    void l;
  }
  const colours: Record<string, string> = {};
  const entries: [Role, string][] = [];
  const mats: Partial<Record<Role, Mat>> = {};
  groups.forEach((g, i) => {
    const role = `c${i}` as Role;
    // The group's base: its middle lightness.
    const sorted = [...g.members].sort((a, b) => toHsl(a[1])[2] - toHsl(b[1])[2]);
    const base = sorted[Math.floor((sorted.length - 1) / 2)]![1];
    const sen = sentinel(role, base);
    entries.push([role, sen]);
    mats[role] = auto(base, 'silk');
    for (const [ch, hex] of g.members) colours[ch] = shade(sen, toHsl(hex)[2] - toHsl(base)[2]);
  });
  const roles = new Roles(entries);
  const dress: Dress = { mats, keepLines: true };
  const base = headwearLayer(id, colours);
  return {
    ...base,
    native: true,
    pieces: (sk, fit) => base.pieces(sk, fit).map((p): Piece => ({ ...p, native: (c) => paintCloth(c, roles, dress) })),
  };
}

// ── outfits ──

/** uniforms.ts `uniformLayers`, native. */
export function nativeUniformLayers(u: Uniform): Layer[] {
  const out: Layer[] = [nBottom(u.bottom), nShoes(u.shoes), nTop(u.top)];
  const r = u.read;
  const read: ReadPass | undefined = r && {
    rim: mat(r.rim, 'silk'),
    folds: r.folds,
    ...(r.ribbon ? { ribbon: mat(r.ribbon, 'silk') } : {}),
    // The rank's motif in the band; a novice's (tier 0, no motif) a plain band between the pipings.
    ...(u.robe
      ? {
          hem: {
            motif: u.robe.hem ? HEM[u.robe.hem] : PLAIN_BAND,
            ground: auto(u.robe.trim),
            thread: auto(u.robe.accent ?? u.robe.inner),
            piping: auto(u.robe.accent ?? '#d6a640'),
          },
        }
      : {}),
  };
  if (u.robe) out.push(nRobe(u.robe, read));
  if (u.belt) out.push(nBelt(u.belt));
  if (u.cape) out.push(nCape(u.cape));
  if (u.pauldrons) out.push(nPauldrons(u.pauldrons));
  for (const h of u.headwear ?? []) out.push(nHeadwear(h));
  if (u.regalia?.length) out.push(nRegalia(u.regalia));
  return out;
}

/** fashion.ts `fashionLayers`, native. */
export function nativeFashionLayers(b: OutfitBody): Layer[] {
  return [...nativeUniformLayers(b), ...(b.extras ?? []).map(nExtra)];
}
